import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import boheme_ecosystem from "../assets/About_boheme.png";
import biorecipe_illustration from "../assets/BioRECIPE_illustration.png"; // adjust filename
import violin_illustration from "../assets/VIOLIN_illustration.png"; // adjust filename
import dish_illustration from "../assets/DISH_illustration.png"; // adjust filename
import celesta_illustration from "../assets/CELESTA_illustration.png"; // adjust filename
import kalimba_illustration from "../assets/KALIMBA_illustration.png"; // adjust filename
import accordion_illustration from "../assets/ACCORDION_illustration.png"; // adjust filename
import clarinet_illustration from "../assets/CLARINET_illustration.png"; // adjust filename
import bell_illustration from "../assets/BELL_illustration.png"; // adjust filename
/*
  ── PITT BRAND COLOR PALETTE ──
  Primary:   Royal Blue #003594, Gold #FFB81C
  Secondary: New Sky #DBEEFF, Gray #C8C9C7, White #FFFFFF,
             Medium Blue #00205B, Bronze #B87333, Black #000000
  Accent:    Golden Haze #FFE939, Light Blue #66B2E3, Light Green #00AD6E,
             Orange #F7911E, Infrared #FF5B45, White Peach #FFDECD, Merlot #770538
*/

const TOOLS_DATA = [
  {
    id: "boheme",
    name: "BOHEME",
    tagline:
      "Bridging Observations to Hypotheses through Executable Model Engineering",
    overview:
      "BOHEME is an AI-enabled ecosystem connecting biological knowledge, executable modeling, and scientific discovery. It transforms scientific observations into structured and verified knowledge, integrates that knowledge into executable models, and enables their simulation and analysis to explain biological behavior, generate predictions, and formulate new hypotheses. BOHEME builds on the lab's earlier DySE framework with a new generation of AI- and LLM-supported tools spanning the modeling and discovery process, including CELESTA, BioRECIPE, VIOLIN, KALIMBA, ACCORDION, CLARINET, and DiSH.",
    format: "",
    diagram: boheme_ecosystem,
    links: [
      { label: "BOHEME Web Interface", url: "https://boheme.pitt.edu/" },
      {
        label: "DySE Framework (previous generation)",
        url: "https://www.nmzlab.pitt.edu/sites/default/files/assets/DySE%202026.png",
      },
    ],
    publication: null,
    citation: "",
    funding: [],
  },
  {
    id: "biorecipe",
    name: "BioRECIPE",
    tagline:
      "Biological system Representation for Evaluation, Curation, Interoperability, Preserving, and Execution",
    overview:
      "A standardized, interoperable schema for representing biological knowledge and executable models in alignment with FAIR principles. BioRECIPE captures biochemical interactions and their biological context in a structured, machine-readable format accessible to computational tools, modelers, and biological domain experts. Read more about BioRECIPE in our ACS Synthetic Biology paper and the detailed ReadTheDocs documentation. Use the BioRECIPE web interface within the BOHEME ecosystem to translate existing representations (e.g., JSON, BioPAX, and SIF) into the BioRECIPE schema, or to create your own interaction lists and models directly in BioRECIPE.",
    format: "",
    diagram: biorecipe_illustration,
    publication: {
      label: "BioRECIPE Publication",
      url: "https://pubs.acs.org/doi/full/10.1021/acssynbio.4c00096",
    },
    citation:
      "Holtzapple, E., Zhou, G., Luo, H., Tang, D., Arazkhani, N., Hansen, C., Telmer, C. A., & Miskov-Zivanov, N. (2024). The BioRECIPE Knowledge Representation Format. ACS Synthetic Biology, 13(8), 2621–2624. https://doi.org/10.1021/acssynbio.4c00096",
    funding: [
      "DARPA Big Mechanism award AIMCancer (W911NF-17-1-0135)",
      "NSF EAGER award CCF-2324742",
    ],
  },
  {
    id: "accordion",
    name: "ACCORDION",
    tagline: "ACCelerating and Optimizing model RecommenDatIONs",
    overview:
      "A model recommendation tool that evaluates and selects relevant knowledge. ACCORDION is a framework for context-aware knowledge selection and automated recommendation of executable biological models. Starting from a baseline model and candidate knowledge retrieved from literature and databases, ACCORDION uses graph-based clustering to identify relevant model extensions, then evaluates candidate models through simulation and formal analysis to recommend models that best reproduce known or desired system behavior. This enables systematic exploration of alternative mechanisms while reducing large collections of candidate interactions to those most relevant to the modeled biological context. Read more about ACCORDION in our Frontiers in Systems Biology paper, explore the detailed ReadTheDocs documentation, access the source code on GitHub, or use the Jupyter notebook to explore ACCORDION workflows and benchmark examples.",
    format: "Works with discrete network models in the BioRECIPE format.",
    diagram: accordion_illustration,
    publication: {
      label: "ACCORDION Publication",
      url: "https://www.frontiersin.org/journals/systems-biology/articles/10.3389/fsysb.2024.1308292/full",
    },
    citation:
      "Ahmed, Y., Telmer, C. A., Zhou, G., & Miskov-Zivanov, N. (2024). Context-aware knowledge selection and reliable model recommendation with ACCORDION. Frontiers in Systems Biology, 4, 1308292. https://doi.org/10.3389/fsysb.2024.1308292",
    funding: [
      "DARPA Big Mechanism award AIMCancer (W911NF-17-1-0135)",
      "University of Pittsburgh, Swanson School of Engineering",
    ],
  },
    {
    id: "bell",
    name: "BELL",
    tagline: "Biomodel Evidence and LLM-based Logic",
    overview:
      "A biocuration support framework that automates evidence retrieval, scoring, and explanation for interaction-level verification of biological knowledge. For each interaction, BELL grounds the entities, queries seven biological databases (INDRA, OmniPath, STRING, SIGNOR, Reactome, BioGRID, and Pathway Commons), and computes interpretable scores for source coverage, attribute match, INDRA belief, and database conflict. A large language model then uses chain-of-thought reasoning over these precomputed scores and the retrieved evidence to explain the result and recommend a curator action: accept, accept with enrichment, flag for review, or reject. Every step, from resolved identifiers to database evidence, scores, and the full LLM prompt and response, remains visible to the curator, supporting transparent and reproducible curation decisions. Read more about BELL in our bioRxiv preprint, and use it through KALIMBA in the BOHEME web interface.",
    format: "Uses BioRECIPE representation format.",
    diagram: bell_illustration,
    publication: {
      label: "BELL Publication",
      url: "https://doi.org/10.64898/2026.09.18.721350",
    },
    citation:
      "Arazkhani, N., Cochran, B., & Miskov-Zivanov, N. (2026). BELL: Biomodel Evidence and LLM-based Logic. bioRxiv. https://doi.org/10.64898/2026.09.18.721350",
    funding: [
      "NIH R01LM014673",
    ],
  },
  {
    id: "celesta",
    name: "CELESTA",
    tagline:
      "Context Extraction through LEarning with Semi-supervised multi-Task Architecture",
    overview:
      "An AI framework for extracting the biological context needed to interpret interactions described in biomedical text. CELESTA uses open-set semi-supervised multi-task learning to identify contextual information associated with biological interactions, including cell type, cell line, disease, organ, and intracellular location. By combining relation and context classification and leveraging both labeled and unlabeled data, CELESTA helps transform literature-derived interactions into richer, context-aware biological knowledge. Read more about CELESTA in our Journal of Biomedical Informatics paper, “An open-set semi-supervised multi-task learning framework for context classification in biomedical texts,” and explore the code, dataset, and implementation documentation on GitHub. Use the CELESTA web interface within the BOHEME ecosystem to extract and assign biological context to interactions from biomedical text.",
    format: "",
    diagram: celesta_illustration,
    publication: {
      label: "CELESTA Publication",
      url: "https://doi.org/10.1016/j.jbi.2025.104886",
    },
    citation:
      "Tang, D., Chow Tam, T. Y., Luo, H., Telmer, C. A., & Miskov-Zivanov, N. (2025). An open-set semi-supervised multi-task learning framework for context classification in biomedical texts. Journal of Biomedical Informatics, 169, 104886. https://doi.org/10.1016/j.jbi.2025.104886",
    funding: ["NSF EAGER award CCF-2324742"],
  },
  {
    id: "clarinet",
    name: "CLARINET",
    tagline: "CLARIfying NETworks",
    overview:
      "A model recommendation tool that evaluates literature-derived event collaboration graphs to identify useful extensions to existing biological network models. CLARINET organizes interactions extracted from the literature into event collaboration graphs and evaluates them using metrics that capture their occurrence and co-occurrence across publications, as well as their connectivity to an existing baseline model. It groups related interactions and ranks candidate extensions, helping modelers identify connected sets of new biological knowledge that can be incorporated into a model. CLARINET is parametrizable, allowing users to explore different criteria for selecting model extensions, and is designed to efficiently process large sets of literature-extracted interactions. Read more about CLARINET in our Bioinformatics Advances paper, explore the ReadTheDocs documentation, access the GitHub repository, or try CLARINET interactively through our Jupyter notebook.",
    format:
      "Extends discrete network models in the BioRECIPE format using knowledge from literature.",
    diagram: clarinet_illustration,
    publication: {
      label: "CLARINET Publication",
      url: "https://pubmed.ncbi.nlm.nih.gov/36700090/",
    },
    citation:
      "Ahmed, Y., Telmer, C. A., & Miskov-Zivanov, N. (2021). CLARINET: efficient learning of dynamic network models from literature. Bioinformatics Advances, 1(1), vbab006. https://doi.org/10.1093/bioadv/vbab006",
    funding: [
      "DARPA Big Mechanism award AIMCancer (W911NF-17-1-0135)",
      "University of Pittsburgh, Swanson School of Engineering",
    ],
  },

  {
    id: "dish",
    name: "DISH",
    tagline: "Discrete Stochastic Heterogeneous simulator",
    overview:
      "A flexible simulator for hybrid biological models with multiple deterministic and stochastic simulation schemes and customizable timing behaviors. DiSH supports hybrid models in which individual elements can have different numbers of discrete activity levels and use either logical or arithmetic update functions, allowing models to combine qualitative regulatory relationships with more detailed quantitative information when available. DiSH supports deterministic and stochastic simulation schemes, with additional flexibility provided by regulator weights, element memory, delayed responses, and spontaneous and unbalancing behaviors. These capabilities enable researchers to explore system dynamics at varying levels of resolution and parametrization without requiring the extensive kinetic parameters typically needed for continuous models. Earlier versions of DiSH were presented at the 2017 and 2021 IEEE Winter Simulation Conferences (WSC'17 paper and WSC'21 paper). We also developed a hardware implementation of DiSH in Verilog and deployed it on an FPGA; read more about this work in our EMBC paper. Stay tuned for DiSH 2.0, coming soon to the BOHEME web interface.",
    format: "",
    diagram: dish_illustration,
    publication: {
      label: "DISH Publication",
      url: "",
    },
    citation: "",
    funding: [
      "DARPA Big Mechanism W911NF-17-1-0135",
      "NSF CAREER Award CCF-2442884",
      "NIH R01LM014673",
    ],
  },

  {
    id: "kalimba",
    name: "KALIMBA",
    tagline:
      "Knowledge-Assisted Literature Mining for Biological Interaction Analysis",
    overview:
      "A human-in-the-loop platform for extracting, curating, and exploring biological interactions from scientific literature. KALIMBA integrates NLP- and LLM-based interaction extraction with expert annotation and evidence-grounded conversational querying in a unified workflow. Users can search the literature, extract interactions using NLP-only, LLM-only, or hybrid approaches, review and correct extracted knowledge, and interactively query the supporting source literature through a retrieval-augmented generation (RAG) chat interface. By keeping domain experts actively involved throughout the process, KALIMBA supports efficient and transparent extraction and curation of biological knowledge from the rapidly growing scientific literature. Read more about KALIMBA in our BioNLP paper, and use it through the web interface within the BOHEME ecosystem.",
    format: "",
    diagram: kalimba_illustration,
    publication: {
      label: "KALIMBA Publication",
      url: "https://aclanthology.org/2026.bionlp-1.71/",
    },
    citation:
      "Arazkhani, N., Kotecki, M., Cochran, B., & Miskov-Zivanov, N. (2026). KALIMBA: Knowledge-Assisted Literature Mining for Biological Interaction Analysis. In Proceedings of the 25th Workshop on Biomedical Natural Language Processing (BioNLP 2026) (pp. 880–889). Association for Computational Linguistics. https://doi.org/10.18653/v1/2026.bionlp-1.71",
    funding: [

      "NIH R01LM014673",
    ],
  },
  {
    id: "violin",
    name: "VIOLIN",
    tagline:
      "Versatile Interaction Organizing to Leverage Information in Networks",
    overview:
      "A modular framework for reconciling new biological knowledge with existing knowledge graphs and mechanistic models. VIOLIN systematically compares literature-derived biological interactions with structured baseline knowledge to determine whether new evidence corroborates, contradicts, or extends what is already known, while accounting for biological context and other interaction attributes. Its configurable graph-based approach supports heterogeneous knowledge sources and alternative reconciliation strategies, enabling transparent and scalable knowledge integration, model verification, refinement, and extension. Read more about VIOLIN in our bioRxiv preprint and use the VIOLIN 2.0 web interface within the BOHEME ecosystem to reconcile your own interaction lists and models.",
    format: "Uses BioRECIPE representation format.",
    diagram: violin_illustration,
    publication: {
      label: "VIOLIN Publication",
      url: "https://www.biorxiv.org/content/10.1101/2024.07.21.604448",
    },
    citation:
      "Luo, H., Hansen, C. E., Arazkhani, N., Telmer, C. A., Tang, D., Zhou, G., Spirtes, P., & Miskov-Zivanov, N. (2024). VIOLIN: A modular framework for scalable reconciliation of heterogeneous interaction graphs. bioRxiv. https://doi.org/10.1101/2024.07.21.604448",
    funding: [
      "DARPA Big Mechanism W911NF-17-1-0135",
      "NSF EAGER Award #2324742",
      "NIH R01LM014673",
    ],
  },




];

const COPYRIGHT_DATA = {
  year: "2026",
  lab: {
    name: "Miskov-Zivanov Lab (MeLoDy Lab)",
    url: "https://www.nmzlab.pitt.edu/",
  },
  institution: "University of Pittsburgh",
  thirdParty: [
    {
      name: "React",
      version: "18.x",
      license: "MIT License",
      url: "https://react.dev",
      copyright: "© Meta Platforms, Inc. and affiliates",
    },
    {
      name: "React Router",
      version: "6.x",
      license: "MIT License",
      url: "https://reactrouter.com",
      copyright: "© Remix Software, Inc.",
    },
    {
      name: "Recharts",
      version: "2.x",
      license: "MIT License",
      url: "https://recharts.org",
      copyright: "© 2015–2024 Recharts Group",
    },
    {
      name: "Flaticon",
      version: "—",
      license: "Flaticon Free License",
      url: "https://www.flaticon.com",
      copyright: "© Flaticon – Icons made by various authors",
    },
    {
      name: "Icons8",
      version: "—",
      license: "Icons8 Free License",
      url: "https://icons8.com",
      copyright: "© Icons8 LLC – Free icons, illustrations & photos",
    },
  ],
  contact: "nmzivanov@pitt.edu",
};

const aboutStyles = `

* { margin: 0; padding: 0; box-sizing: border-box; }

body {
  font-family: Arial, sans-serif;
  background: #f4f5f7;
  min-height: 100vh;
}

.about-page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
}

.about-body-layout {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}

/* ── Sidebar ── */
.about-sidebar {
  width: 250px;
  min-width: 250px;
  background-color: #00205B;
  color: #DBEEFF;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.about-sidebar-brand {
  padding: 40px 18px 26px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #003594;
}

.about-sidebar-brand-title {
  font-size: 30px;
  color: #FFFFFF;
  letter-spacing: 1px;
  line-height: 1;
}

.about-sidebar-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #FFFFFF;
  margin-bottom: 6px;
  padding: 30px 10px 10px;
  border-bottom: 1px solid #FFFFFF;
}

.about-sidebar-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 10px 18px;
  font-size: 13px;
  color: #DBEEFF;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  text-decoration: none;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}

.about-sidebar-item:hover {
  background: rgba(255,184,28,0.1);
  color: #fff;
}

.about-sidebar-item.active {
  background: rgba(255,184,28,0.15);
  color: #FFB81C;
  font-weight: 600;
}

.about-sidebar-item-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #66B2E3;
  flex-shrink: 0;
  transition: background 0.15s;
}

.about-sidebar-item.active .about-sidebar-item-dot,
.about-sidebar-item:hover .about-sidebar-item-dot {
  background: #FFB81C;
}

.about-sidebar-footer {
  margin-top: auto;
  padding: 16px 18px;
  border-top: 1px solid rgba(255,255,255,0.08);
}

.about-sidebar-footer-text {
  font-size: 14px;
  color: #FFFFFF;
  line-height: 1.5;
}

/* ── Main Area ── */
.about-main-area {
  flex: 1;
  padding: 50px 40px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;
  min-width: 0;
  background: #f4f5f7;
}

.about-page-heading {
  font-size: 22px;
  font-weight: 700;
  color: #00205B;
}

/* ── Tool Content ── */
.about-tool-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.about-tool-name {
  font-size: 28px;
  font-weight: 800;
  color: #003594;
  font-family: 'Mystery Quest', cursive;
  letter-spacing: 5px;
}

.about-tool-tagline {
  display: inline-block;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #003594;
  background-color: #DBEEFF;
  border-radius: 20px;
  width: fit-content;
  line-height: 1.4;
}

.about-card {
  background: #fff;
  border: 2px solid #C8C9C7;
  border-radius: 6px;
  padding: 20px 24px;
  transition: border-color 0.2s;
}

.about-card-title {
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #003594;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.about-card-text {
  font-size: 14px;
  color: #111827;
  line-height: 1.7;
  white-space: pre-line;
}

.about-card-divider {
  border: none;
  border-top: 1px solid #E5E7EB;
  margin: 20px 0;
}

.about-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.about-format-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  background: #DBEEFF;
  border: 2px dashed #003594;
  border-radius: 6px;
}

.about-format-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #003594;
  border-radius: 6px;
  color: #FFB81C;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.about-format-text {
  font-size: 13px;
  font-weight: 600;
  color: #00205B;
}

.about-publication-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0px;
  color: #00205B;
  font-size: 13px;
  font-weight: 700;
  width: fit-content;
}

.about-publication-link:hover {
  color: #FFB81C;
}
  .about-publication-link-temp {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0px;
  color: #00205B;
  font-size: 13px;
  font-weight: 700;
  width: fit-content;
}

/* ── Cite Section ── */
.about-cite-box {
  background: #f9fafb;
  border: 1px solid #E5E7EB;
  border-left: 4px solid #003594;
  border-radius: 4px;
  padding: 14px 16px;
  font-size: 12.5px;
  color: #1f2937;
  line-height: 1.7;
  font-family: 'Courier New', monospace;
  margin-bottom: 10px;
}

.about-cite-copy-btn {
  padding: 6px 16px;
  border: 1px solid #003594;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  background: #fff;
  color: #003594;
  transition: all 0.2s;
}

.about-cite-copy-btn:hover {
  background: #DBEEFF;
}

.about-cite-copy-btn.copied {
  background: #00AD6E;
  border-color: #00AD6E;
  color: #fff;
}

.about-diagram-img {
  width: 100%;
  height: auto;
  display: block;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  margin-bottom: 4px;
}

/* ── Funding Section ── */
.about-funding-section {
  background: #fff;
  border: 2px solid #C8C9C7;
  border-radius: 6px;
  padding: 20px 24px;
}

.about-funding-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #003594;
  margin-bottom: 12px;
}

.about-funding-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 13px;
  color: #111827;
  line-height: 1.5;
}

.about-funding-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #FFB81C;
  flex-shrink: 0;
}

/* ── Welcome / default state ── */
.about-welcome {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #C8C9C7;
  text-align: center;
  padding: 60px 20px;
}

.about-welcome-icon { font-size: 56px; opacity: 0.4; }
.about-welcome-title { font-size: 22px; font-weight: 700; color: #003594; }
.about-welcome-subtitle { font-size: 14px; color: #00205B; max-width: 420px; line-height: 1.6; }

/* ── Copyright Page ── */
.copyright-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 4px;
}

.copyright-page-name {
  font-size: 28px;
  font-weight: 800;
  color: #003594;
  font-family: 'Mystery Quest', cursive;
  letter-spacing: 4px;
}

.copyright-page-subtitle {
  display: inline-block;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #003594;
  background-color: #DBEEFF;
  border-radius: 20px;
  width: fit-content;
  line-height: 1.4;
}

.copyright-notice-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 22px;
  background: #00205B;
  border-radius: 6px;
  color: #DBEEFF;
}

.copyright-notice-icon {
  font-size: 28px;
  flex-shrink: 0;
  line-height: 1;
  margin-top: 2px;
}

.copyright-notice-body {}

.copyright-notice-main {
  font-size: 15px;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 4px;
}

.copyright-notice-sub {
  font-size: 13px;
  color: rgba(219,238,255,0.85);
  line-height: 1.6;
}

.copyright-notice-sub a {
  color: #FFB81C;
  text-decoration: none;
  font-weight: 600;
}

.copyright-notice-sub a:hover {
  text-decoration: underline;
}

.copyright-license-box {
  background: #f9fafb;
  border: 1px solid #E5E7EB;
  border-left: 4px solid #003594;
  border-radius: 4px;
  padding: 16px 18px;
  font-size: 12px;
  color: #374151;
  line-height: 1.75;
  white-space: pre-line;
  font-family: 'Courier New', monospace;
}

.copyright-third-party-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.copyright-third-party-table thead tr {
  background: #003594;
  color: #fff;
}

.copyright-third-party-table th {
  padding: 10px 14px;
  text-align: left;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.copyright-third-party-table tbody tr {
  border-bottom: 1px solid #E5E7EB;
  transition: background 0.12s;
}

.copyright-third-party-table tbody tr:last-child {
  border-bottom: none;
}

.copyright-third-party-table tbody tr:hover {
  background: #f0f6ff;
}

.copyright-third-party-table td {
  padding: 10px 14px;
  color: #111827;
  vertical-align: middle;
}

.copyright-third-party-table td a {
  color: #003594;
  text-decoration: none;
  font-weight: 600;
}

.copyright-third-party-table td a:hover {
  color: #FFB81C;
  text-decoration: underline;
}

.copyright-license-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #DBEEFF;
  color: #00205B;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
}

.copyright-contact-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #111827;
  margin-top: 4px;
}

.copyright-contact-row a {
  color: #003594;
  font-weight: 700;
  text-decoration: none;
}

.copyright-contact-row a:hover {
  color: #FFB81C;
}
`;

function CiteBox({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      console.warn("Copy failed:", e);
    }
  };

  return (
    <>
      <div className="about-card-title">Cite</div>
      <div className="about-cite-box">{text}</div>
      <button
        className={`about-cite-copy-btn${copied ? " copied" : ""}`}
        onClick={handleCopy}
      >
        {copied ? "✓ Copied" : "Copy Citation"}
      </button>
    </>
  );
}

function CopyrightPage() {
  return (
    <>
      {/* Header */}
      {/* <div className="copyright-header">
        <div className="copyright-page-name">Copyright</div>
        <div className="copyright-page-subtitle">
          Legal notices &amp; third-party attributions
        </div>
      </div> */}

      {/* Main copyright notice */}
      <div className="copyright-notice-banner">
        <div className="copyright-notice-icon">©</div>
        <div className="copyright-notice-body">
          <div className="copyright-notice-main">
            © {COPYRIGHT_DATA.year} {COPYRIGHT_DATA.lab.name},{" "}
            {COPYRIGHT_DATA.institution}
          </div>
          <div className="copyright-notice-sub">
            Developed and maintained by the{" "}
            <a
              href={COPYRIGHT_DATA.lab.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {COPYRIGHT_DATA.lab.name}
            </a>{" "}
            at the {COPYRIGHT_DATA.institution}.
          </div>
        </div>
      </div>

      {/* License card */}
      {/* <div className="about-card">
        <div className="about-card-title">License</div>
        <div className="about-card-text" style={{ marginBottom: "14px" }}>
          This software is distributed under the{" "}
          <strong>{COPYRIGHT_DATA.license.name}</strong>.
        </div>
        <div className="copyright-license-box">{COPYRIGHT_DATA.license.text}</div>
      </div> */}

      {/* Third-party attributions card */}
      <div className="about-card">
        <div className="about-card-title">Third-Party Attributions</div>
        <div
          className="about-card-text"
          style={{ marginBottom: "14px", color: "#6B7280" }}
        >
          This project makes use of the following open-source libraries and
          tools, each governed by their respective licenses.
        </div>
        <table className="copyright-third-party-table">
          <thead>
            <tr>
              <th>Library</th>
              <th>Version</th>
              <th>License</th>
              <th>Copyright</th>
            </tr>
          </thead>
          <tbody>
            {COPYRIGHT_DATA.thirdParty.map((lib) => (
              <tr key={lib.name}>
                <td>
                  <a href={lib.url} target="_blank" rel="noopener noreferrer">
                    {lib.name}
                  </a>
                </td>
                <td style={{ color: "#6B7280" }}>{lib.version}</td>
                <td>
                  <span className="copyright-license-badge">{lib.license}</span>
                </td>
                <td style={{ color: "#6B7280" }}>{lib.copyright}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Contact card */}
      <div className="about-card">
        <div className="about-card-title">Contact</div>
        <div className="about-card-text" style={{ marginBottom: "8px" }}>
          For licensing inquiries or to report a copyright concern, please reach
          out to:
        </div>
        <div className="copyright-contact-row">
          <a href={`mailto:${COPYRIGHT_DATA.contact}`}>
            {COPYRIGHT_DATA.contact}
          </a>
        </div>
      </div>
    </>
  );
}

export default function About() {
  const location = useLocation();
  const navigate = useNavigate();

  const initialTool = location.state?.fromTool || null;
  const initialView = location.state?.view || null; // "copyright" | null
  const [activeTool, setActiveTool] = useState(initialTool);
  const [activeView, setActiveView] = useState(initialView); // "copyright" | null

  const activeData = TOOLS_DATA.find((t) => t.id === activeTool);
  const bohemeData = TOOLS_DATA.find((t) => t.id === "boheme");
  const sidebarTools = TOOLS_DATA.filter((t) => t.id !== "boheme");
  // When no specific tool is selected (and we're not on the copyright page),
  // BOHEME acts as the default/home content instead of a generic placeholder.
  const displayData = activeData || bohemeData;

  const handleToolClick = (id) => {
    setActiveTool(id);
    setActiveView(null);
    navigate("/about", { state: { fromTool: id }, replace: true });
  };

  const handleCopyrightClick = () => {
    setActiveView("copyright");
    setActiveTool(null);
    navigate("/about", { state: { view: "copyright" }, replace: true });
  };

  return (
    <>
      <style>{aboutStyles}</style>
      <div className="about-page-wrapper">
        <div className="about-body-layout">
          {/* ── Sidebar ── */}
          <nav className="about-sidebar">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {/* Tools section */}
              <div>
                <div className="about-sidebar-section-title">About</div>
                {sidebarTools.map((tool) => (
                  <button
                    key={tool.id}
                    className={`about-sidebar-item ${activeTool === tool.id ? "active" : ""}`}
                    onClick={() => handleToolClick(tool.id)}
                  >
                    <span className="about-sidebar-item-dot" />
                    <span>{tool.name}</span>
                  </button>
                ))}
              </div>

              {/* Legal section */}
              <div style={{ marginTop: "auto" }}>
                <div className="about-sidebar-section-title">Legal</div>
                <button
                  className={`about-sidebar-item ${activeView === "copyright" ? "active" : ""}`}
                  onClick={handleCopyrightClick}
                >
                  <span className="about-sidebar-item-dot" />
                  <span>Copyright &amp; Attributions</span>
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="about-sidebar-footer">
              <div className="about-sidebar-footer-text">
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "10px",
                    color: "rgba(219,238,255,0.7)",
                  }}
                >
                  © 2026{" "}
                  <Link
                    to="https://www.nmzlab.pitt.edu/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "rgba(219,238,255,0.7)" }}
                  >
                    Miskov-Zivanov Lab (MeLoDy lab)
                  </Link>
                </div>
              </div>
            </div>
          </nav>

          {/* ── Main Content ── */}
          <main className="about-main-area">
            {activeView === "copyright" ? (
              <CopyrightPage />
            ) : (
              <>
                {/* Tool Header */}
                <div className="about-tool-header">
                  <div className="about-tool-name">{displayData.name}</div>
                  <div className="about-tool-tagline">
                    {displayData.tagline}
                  </div>
                </div>

                {/* Single unified card */}
                <div className="about-card">
                  {/* Overview */}
                  <div className="about-card-title">Overview</div>
                  <div className="about-card-text">{displayData.overview}</div>

                  {displayData.diagram && (
                    <img
                      src={displayData.diagram}
                      alt={`${displayData.name} ecosystem diagram`}
                      className="about-diagram-img"
                      style={{ marginTop: "16px" }}
                    />
                  )}

                  <hr className="about-card-divider" />

                  {/* Format */}
                  {displayData.format && (
                    <>
                      <div className="about-card-title">Format</div>
                      <div className="about-card-text">
                        {displayData.format}
                      </div>
                      <hr className="about-card-divider" />
                    </>
                  )}

                  {/* Links */}
                  {displayData.links && displayData.links.length > 0 && (
                    <>
                      <div className="about-card-title">Links</div>
                      {displayData.links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="about-publication-link"
                          style={{ display: "flex" }}
                        >
                          {link.label}
                        </a>
                      ))}
                      <hr className="about-card-divider" />
                    </>
                  )}

                  {/* Publication */}
                  {displayData.publication && (
                    <>
                      <div className="about-card-title">Publication</div>
                      {displayData.publication.url ? (
                        <a
                          href={displayData.publication.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="about-publication-link"
                        >
                          {displayData.publication.label}
                        </a>
                      ) : (
                        <span className="about-publication-link-temp">
                          {displayData.publication.label}
                        </span>
                      )}
                      <hr className="about-card-divider" />
                    </>
                  )}

                  {/* Cite */}
                  {displayData.citation && (
                    <>
                      <CiteBox text={displayData.citation} />
                      <hr className="about-card-divider" />
                    </>
                  )}

                  {/* Funding */}
                  {displayData.funding && displayData.funding.length > 0 && (
                    <>
                      <div className="about-card-title">Funding</div>
                      {displayData.funding.map((item, idx) => (
                        <div className="about-funding-item" key={idx}>
                          <span className="about-funding-dot" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </>
  );
}
